<script setup lang="ts">
import { useWindowSize } from '@vueuse/core';
import { computed, ref, watch } from 'vue';

import Drawer from '@/shared/components/UI/Drawer.vue';
import Modal from '@/shared/components/UI/Modal.vue';
import StockIndicator from '@/shared/components/UI/StockIndicator.vue';
import { Variant } from '@/shared/composables/patternProducts/patternProducts.types';
import { useTranslations } from '@/shared/composables/useTranslations';
import { useViewport } from '@/shared/composables/useViewport';
import { breakpoints } from '@/shared/constants/constants';
const { width } = useWindowSize();

import VariantSelectorList from './VariantSelectorList.vue';

const { t } = useTranslations();

const props = withDefaults(
  defineProps<{
    heading?: string;
    options?: Variant[];
    active: Variant | null;
    placeholder?: string;
    productType: string;
  }>(),
  {
    heading: '',
    options: () => [],
    placeholder: ''
  }
);

const isOpen = ref(false);
const activeOption = ref<Variant | null>(props.active);

const { isMobile } = useViewport();

watch(isMobile, (newVal) => {
  if (!newVal) {
    isOpen.value = false;
  }
});

watch(
  () => props.active,
  (newVal) => {
    activeOption.value = newVal;
  }
);

const onClick = () => {
  isOpen.value = true;
};

const emit = defineEmits(['option-click']);

const onOptionClick = (option: Variant) => {
  activeOption.value = option;
  isOpen.value = false;
  emit('option-click', option);
};

const modalType = computed(() => {
  return width.value < breakpoints.md ? Drawer : Modal;
});
const modalProps = computed(() => {
  return modalType.value === Modal
    ? {
        size: props.productType === 'yarn' ? '2xl' : 'md',
        alwaysOnTop: true,
        closeButton: true
      }
    : {};
});
</script>

<template>
  <div class="w-full grow">
    <div class="relative flex w-full grow items-center justify-start text-left">
      <div v-if="activeOption" class="flex w-full justify-between">
        <slot name="selected-option" :option="activeOption">
          <div class="flex flex-col gap-1.5 text-xs">
            <span>
              <span
                class=""
                :class="
                  activeOption?.quantityAvailable <= 0
                    ? 'text-gray-400 line-through'
                    : 'text-gray-500'
                "
              >
                {{ activeOption.title }}
              </span>
              &nbsp;
              <StockIndicator
                class="inline"
                :in-stock="activeOption.quantityAvailable"
              />
            </span>
            <div>
              <button
                class="inline appearance-none text-xs font-semibold text-black underline"
                aria-haspopup="dialog"
                :aria-expanded="isOpen"
                @click="onClick"
              >
                {{
                  productType === 'yarn'
                    ? t('patterns.products.change-color')
                    : t('patterns.products.change')
                }}
              </button>
            </div>
          </div>
        </slot>
      </div>
      <div v-else>{{ placeholder }}</div>
    </div>

    <component :is="modalType" v-model:active="isOpen" v-bind="modalProps">
      <div class="relative isolate flex flex-col">
        <div class="sticky top-0 z-10 bg-white p-4 font-bold">
          {{ heading }}
        </div>
        <div class="h-full overflow-y-auto">
          <slot name="options" :on-option-click="onOptionClick">
            <VariantSelectorList
              :options="options"
              :active="activeOption"
              :product-type="productType"
              @click="onOptionClick"
            />
          </slot>
        </div>
      </div>
    </component>
  </div>
</template>
